/**
 * Designer-invoice hours shown on the project timer.
 * Cash stays the invoice dollar amount; these hours are informational and may overlap CRM time.
 */

import type { ProjectTimeEntry } from '../types';
import type { DesignerInvoiceDoc, DesignerInvoiceLine } from './designerInvoiceService';
import {
  designerInvoiceCoveredByQbExpense,
  qbExpenseDedupeSets,
} from './designerInvoicePL';
import { expenseAmountForDeal, type ProjectPlMatchExpense } from './projectPlPoQbMatch';

export type DesignerInvoiceTimePo = {
  id: string;
  poNumber?: string;
  projectId?: string;
  designerInvoiceId?: string;
};

export type DesignerInvoiceTimeLink = {
  key: string;
  hours: number;
  /** Invoice line cash. Not added into the timer total. */
  amount: number;
  purchaseRequisitionId?: string;
  poNumber?: string;
  /** Link text: PO number when one exists, otherwise the invoice number. */
  label: string;
};

export type DesignerInvoiceTimeChartPoint = {
  minutes: number;
  date: Date;
};

export type DesignerInvoiceTimeGroup = {
  vendor: string;
  minutes: number;
  links: DesignerInvoiceTimeLink[];
  chartPoints: DesignerInvoiceTimeChartPoint[];
};

export function normalizePersonName(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

export function matchInvoiceVendorToUserId(
  vendor: string,
  users: Array<{ userId: string; userName: string }>,
): string | null {
  const name = normalizePersonName(vendor);
  if (!name) return null;
  const hit = users.find((user) => normalizePersonName(user.userName) === name);
  return hit?.userId ?? null;
}

export type InvoicePerson = {
  userId: string;
  userName: string;
  company?: string;
  /** Already has CRM time on this project. */
  onProject?: boolean;
};

/**
 * Profile company is the design firm. "Sojourn" matches an invoice from
 * "Sojourn Timber Design", and several designers can share that company.
 */
export function companyMatchesDesignFirm(company: string, vendor: string): boolean {
  const norm = (value: string) =>
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(
        /\b(design|designs|designer|designers|studio|studios|architects|architect|architecture|timber|frames|frame|llc|inc|co|company|the)\b/g,
        ' ',
      )
      .replace(/\s+/g, ' ')
      .trim();
  const left = norm(company);
  const right = norm(vendor);
  if (!left || !right) return false;
  return left === right || right.startsWith(`${left} `) || left.startsWith(`${right} `);
}

export type InvoiceVendorAssignment =
  | { kind: 'person'; userId: string; userName: string }
  | { kind: 'firm'; userIds: string[]; names: string[] }
  | { kind: 'unmatched' };

function peopleForVendor(vendor: string, people: InvoicePerson[]): InvoicePerson[] {
  const name = normalizePersonName(vendor);
  const byName = people.filter((person) => normalizePersonName(person.userName) === name);
  if (byName.length) return byName;
  const byCompany = people.filter((person) => companyMatchesDesignFirm(person.company || '', vendor));
  const onProject = byCompany.filter((person) => person.onProject);
  return onProject.length ? onProject : byCompany;
}

/** One designer gets the hours. Several designers at the firm share one row so hours are not multiplied. */
export function assignInvoiceVendor(vendor: string, people: InvoicePerson[]): InvoiceVendorAssignment {
  const matches = peopleForVendor(vendor, people);
  if (matches.length === 1) {
    return { kind: 'person', userId: matches[0].userId, userName: matches[0].userName };
  }
  if (matches.length > 1) {
    return {
      kind: 'firm',
      userIds: matches.map((person) => person.userId),
      names: matches.map((person) => person.userName),
    };
  }
  return { kind: 'unmatched' };
}

export type TrackerTimeRow = {
  userId: string;
  userName: string;
  minutes: number;
  importedMinutes: number;
  links: DesignerInvoiceTimeLink[];
  /** Designers at the firm when the invoice is not folded onto one person. */
  memberNames?: string[];
};

export type InvoiceHourPlacement = {
  vendor: string;
  userId: string;
  userName: string;
};

export function placeDesignerInvoiceHours(
  tracked: Array<{ userId: string; userName: string; minutes: number }>,
  groups: DesignerInvoiceTimeGroup[],
  people: InvoicePerson[],
): { rows: TrackerTimeRow[]; placements: InvoiceHourPlacement[] } {
  const directory = new Map<string, InvoicePerson>();
  people.forEach((person) => directory.set(person.userId, { ...person }));
  tracked.forEach((row) => {
    const existing = directory.get(row.userId);
    if (existing) existing.onProject = true;
    else directory.set(row.userId, { userId: row.userId, userName: row.userName, onProject: true });
  });
  const roster = Array.from(directory.values());

  const rows = new Map<string, TrackerTimeRow>();
  tracked.forEach((row) => {
    rows.set(row.userId, {
      userId: row.userId,
      userName: row.userName,
      minutes: row.minutes,
      importedMinutes: 0,
      links: [],
    });
  });

  const placements: InvoiceHourPlacement[] = [];
  groups.forEach((group) => {
    const assignment = assignInvoiceVendor(group.vendor, roster);
    if (assignment.kind === 'person') {
      const current = rows.get(assignment.userId);
      if (current) {
        current.minutes += group.minutes;
        current.importedMinutes += group.minutes;
        current.links.push(...group.links);
      } else {
        rows.set(assignment.userId, {
          userId: assignment.userId,
          userName: assignment.userName,
          minutes: group.minutes,
          importedMinutes: group.minutes,
          links: [...group.links],
        });
      }
      placements.push({
        vendor: group.vendor,
        userId: assignment.userId,
        userName: rows.get(assignment.userId)?.userName || assignment.userName,
      });
      return;
    }

    const userId = invoiceVendorUserId(group.vendor);
    const memberNames = assignment.kind === 'firm' ? assignment.names : undefined;
    const current = rows.get(userId);
    if (current) {
      current.minutes += group.minutes;
      current.importedMinutes += group.minutes;
      current.links.push(...group.links);
      if (memberNames?.length) current.memberNames = memberNames;
    } else {
      rows.set(userId, {
        userId,
        userName: group.vendor,
        minutes: group.minutes,
        importedMinutes: group.minutes,
        links: [...group.links],
        memberNames,
      });
    }
    placements.push({ vendor: group.vendor, userId, userName: group.vendor });
  });

  return {
    rows: Array.from(rows.values()).sort((a, b) => b.minutes - a.minutes),
    placements,
  };
}

/** Stable id for a vendor who has invoice hours but no CRM time on this project. */
export function invoiceVendorUserId(vendor: string): string {
  const slug = normalizePersonName(vendor).replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'vendor';
  return `invoice-vendor:${slug}`;
}

export function isInvoiceVendorUserId(userId: string): boolean {
  return userId.startsWith('invoice-vendor:');
}

function linesForInvoice(invoice: DesignerInvoiceDoc): DesignerInvoiceLine[] {
  if (invoice.allocations && invoice.allocations.length) return invoice.allocations;
  return invoice.proposedLines || [];
}

function projectHoursLines(invoice: DesignerInvoiceDoc, projectId: string): DesignerInvoiceLine[] {
  return linesForInvoice(invoice).filter((line) => {
    const hours = Number(line.hours);
    return (line.projectId || '').trim() === projectId && Number.isFinite(hours) && hours > 0;
  });
}

function invoiceChartDate(invoice: DesignerInvoiceDoc): Date | null {
  const iso = (invoice.invoiceDate || '').trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  return invoice.approvedAt || null;
}

function poNumberAt(invoice: DesignerInvoiceDoc, requisitionId: string | undefined): string | undefined {
  if (!requisitionId) return undefined;
  const ids = invoice.purchaseRequisitionIds || [];
  const index = ids.indexOf(requisitionId);
  if (index < 0) return undefined;
  const number = invoice.poNumbers?.[index];
  return number?.trim() || undefined;
}

function poForInvoice(
  invoice: DesignerInvoiceDoc,
  projectId: string,
  requisitions: DesignerInvoiceTimePo[],
): { id?: string; poNumber?: string } {
  const ids = invoice.purchaseRequisitionIds || [];
  const owned = requisitions.filter(
    (row) => ids.includes(row.id) || (row.designerInvoiceId && row.designerInvoiceId === invoice.id),
  );
  const onProject = owned.filter((row) => !row.projectId || row.projectId === projectId);
  const pick = onProject[0] || (ids.length === 1 ? owned[0] : undefined);
  if (pick) {
    return { id: pick.id, poNumber: pick.poNumber?.trim() || poNumberAt(invoice, pick.id) };
  }
  if (ids.length === 1) {
    return { id: ids[0], poNumber: invoice.poNumbers?.[0]?.trim() || undefined };
  }
  return {};
}

/**
 * Approved design/engineering invoice hours as dated timer rows.
 * Same person/firm placement as the project timer. Each line keeps the invoice date
 * so a weekly report only counts hours invoiced in that window.
 */
export function designerInvoiceReportEntries(input: {
  invoices: DesignerInvoiceDoc[];
  people: InvoicePerson[];
  trackedUserIdsByProject?: Map<string, Set<string>>;
}): Array<Pick<ProjectTimeEntry, 'id' | 'projectId' | 'userId' | 'userName' | 'minutes' | 'date' | 'createdAt'>> {
  const tracked = input.trackedUserIdsByProject;
  const out: Array<Pick<ProjectTimeEntry, 'id' | 'projectId' | 'userId' | 'userName' | 'minutes' | 'date' | 'createdAt'>> = [];

  for (const invoice of input.invoices) {
    if (invoice.status !== 'approved') continue;
    const date = invoiceChartDate(invoice);
    if (!date) continue;
    const vendor = (invoice.vendor || '').trim() || 'Invoice';

    for (const line of linesForInvoice(invoice)) {
      const projectId = (line.projectId || '').trim();
      const hours = Number(line.hours);
      if (!projectId || !Number.isFinite(hours) || hours <= 0) continue;

      const people = input.people.map((person) => ({
        ...person,
        onProject: !!tracked?.get(projectId)?.has(person.userId),
      }));
      const assignment = assignInvoiceVendor(vendor, people);
      const userId = assignment.kind === 'person' ? assignment.userId : invoiceVendorUserId(vendor);
      const userName = assignment.kind === 'person' ? assignment.userName : vendor;

      out.push({
        id: `invoice:${invoice.id}:${line.id}`,
        projectId,
        userId,
        userName,
        minutes: hours * 60,
        date,
        createdAt: date,
      });
    }
  }

  return out;
}

export type DesignerQbExpenseTimeRow = ProjectPlMatchExpense & {
  purchaseRequisitionId?: string | null;
};

export type DesignerHourlyRateRow = {
  userId: string;
  userName: string;
  hourlyRate: number;
};

export function designerHoursFromAmount(amount: number, hourlyRate: number): number | null {
  if (!Number.isFinite(hourlyRate) || hourlyRate <= 0) return null;
  const hours = (Number(amount) || 0) / hourlyRate;
  if (!Number.isFinite(hours) || hours <= 0) return null;
  return hours;
}

function hourlyRateForVendor(vendor: string, rates: DesignerHourlyRateRow[]): number | null {
  const name = normalizePersonName(vendor);
  const hit = rates.find(row => normalizePersonName(row.userName) === name);
  if (hit?.hourlyRate && hit.hourlyRate > 0) return hit.hourlyRate;
  const firm = rates.find(row => row.hourlyRate > 0);
  return firm?.hourlyRate ?? null;
}

function qbExpenseLinkedInvoiceId(
  expense: DesignerQbExpenseTimeRow,
  invoices: DesignerInvoiceDoc[],
  requisitions: DesignerInvoiceTimePo[],
): string | null {
  const poId = (expense.purchaseRequisitionId || '').trim();
  if (poId) {
    const req = requisitions.find(row => row.id === poId);
    if (req?.designerInvoiceId) return req.designerInvoiceId;
  }
  for (const inv of invoices) {
    if (designerInvoiceCoveredByQbExpense(inv, { qbExpenseIds: new Set([expense.id]) })) return inv.id;
    const qbId = (inv.qboBillId || '').trim();
    if (qbId && (expense.quickbooksId || '').trim() === qbId) return inv.id;
    const ids = inv.purchaseRequisitionIds || [];
    if (poId && ids.includes(poId)) return inv.id;
  }
  return null;
}

export function collectDesignerQbExpenseProjectTime(input: {
  dealId?: string | null;
  projectId: string;
  invoices: DesignerInvoiceDoc[];
  requisitions: DesignerInvoiceTimePo[];
  qbExpenses: DesignerQbExpenseTimeRow[];
  hourlyRates: DesignerHourlyRateRow[];
}): { groups: DesignerInvoiceTimeGroup[]; coveredInvoiceIds: Set<string> } {
  const dealId = (input.dealId || '').trim();
  const dedupe = qbExpenseDedupeSets(input.qbExpenses);
  const coveredInvoiceIds = new Set<string>();
  const groups = new Map<string, DesignerInvoiceTimeGroup>();

  for (const expense of input.qbExpenses) {
    const invoiceId = qbExpenseLinkedInvoiceId(expense, input.invoices, input.requisitions);
    const invoice = invoiceId ? input.invoices.find(row => row.id === invoiceId) : undefined;
    const vendor = (invoice?.vendor || expense.vendorName || '').trim() || 'Invoice';
    if (invoice && designerInvoiceCoveredByQbExpense(invoice, dedupe)) {
      coveredInvoiceIds.add(invoice.id);
    } else if (invoiceId) {
      coveredInvoiceIds.add(invoiceId);
    }

    let amount = 0;
    if (dealId) {
      amount = expenseAmountForDeal(expense, dealId);
    } else if ((expense.purchaseRequisitionId || '').trim()) {
      amount = Number(expense.totalAmount) || 0;
    }
    if (amount <= 0) continue;

    const rate = hourlyRateForVendor(vendor, input.hourlyRates);
    const hours = rate ? designerHoursFromAmount(amount, rate) : null;
    if (!hours) continue;

    const key = normalizePersonName(vendor) || vendor;
    let group = groups.get(key);
    if (!group) {
      group = { vendor, minutes: 0, links: [], chartPoints: [] };
      groups.set(key, group);
    }

    const po = invoice ? poForInvoice(invoice, input.projectId.trim(), input.requisitions) : {};
    const iso = (expense.txnDate || '').trim();
    const date = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
      ? new Date(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)))
      : null;

    group.minutes += hours * 60;
    if (date) group.chartPoints.push({ minutes: hours * 60, date });
    group.links.push({
      key: `qb:${expense.id}`,
      hours,
      amount,
      purchaseRequisitionId: po.id,
      poNumber: po.poNumber,
      label: po.poNumber || (invoice?.invoiceNumber ? `Invoice ${invoice.invoiceNumber}` : 'QB bill'),
    });
  }

  return { groups: Array.from(groups.values()), coveredInvoiceIds };
}

export function mergeDesignerProjectTimeGroups(
  invoiceGroups: DesignerInvoiceTimeGroup[],
  qbGroups: DesignerInvoiceTimeGroup[],
): DesignerInvoiceTimeGroup[] {
  const merged = new Map<string, DesignerInvoiceTimeGroup>();
  const touch = (group: DesignerInvoiceTimeGroup) => {
    const key = normalizePersonName(group.vendor) || group.vendor;
    const cur = merged.get(key);
    if (!cur) {
      merged.set(key, {
        vendor: group.vendor,
        minutes: group.minutes,
        links: [...group.links],
        chartPoints: [...group.chartPoints],
      });
      return;
    }
    cur.minutes += group.minutes;
    cur.links.push(...group.links);
    cur.chartPoints.push(...group.chartPoints);
  };
  invoiceGroups.forEach(touch);
  qbGroups.forEach(touch);
  return Array.from(merged.values());
}

export function collectDesignerProjectTimeForHub(input: {
  projectId: string;
  dealId?: string | null;
  invoices: DesignerInvoiceDoc[];
  requisitions?: DesignerInvoiceTimePo[];
  qbExpenses?: DesignerQbExpenseTimeRow[];
  hourlyRates?: DesignerHourlyRateRow[];
}): DesignerInvoiceTimeGroup[] {
  const requisitions = input.requisitions || [];
  const qbExpenses = input.qbExpenses || [];
  const hourlyRates = input.hourlyRates || [];
  const dedupe = qbExpenseDedupeSets(qbExpenses);
  const skipInvoices = new Set(
    input.invoices
      .filter(inv => designerInvoiceCoveredByQbExpense(inv, dedupe))
      .map(inv => inv.id),
  );

  const invoiceGroups = collectDesignerInvoiceProjectTime({
    projectId: input.projectId,
    invoices: input.invoices.filter(inv => !skipInvoices.has(inv.id)),
    requisitions,
  });

  if (!qbExpenses.length || !hourlyRates.length) return invoiceGroups;

  const qb = collectDesignerQbExpenseProjectTime({
    dealId: input.dealId,
    projectId: input.projectId,
    invoices: input.invoices,
    requisitions,
    qbExpenses,
    hourlyRates,
  });
  qb.coveredInvoiceIds.forEach(id => skipInvoices.add(id));

  const filteredInvoiceGroups = collectDesignerInvoiceProjectTime({
    projectId: input.projectId,
    invoices: input.invoices.filter(inv => !skipInvoices.has(inv.id)),
    requisitions,
  });

  return mergeDesignerProjectTimeGroups(filteredInvoiceGroups, qb.groups);
}

export function collectDesignerInvoiceProjectTime(input: {
  projectId: string;
  invoices: DesignerInvoiceDoc[];
  requisitions?: DesignerInvoiceTimePo[];
}): DesignerInvoiceTimeGroup[] {
  const projectId = input.projectId.trim();
  if (!projectId) return [];
  const requisitions = input.requisitions || [];
  const groups = new Map<string, DesignerInvoiceTimeGroup>();

  for (const invoice of input.invoices) {
    if (invoice.status !== 'approved') continue;
    const lines = projectHoursLines(invoice, projectId);
    if (!lines.length) continue;

    const vendor = (invoice.vendor || '').trim() || 'Invoice';
    const key = normalizePersonName(vendor) || vendor;
    let group = groups.get(key);
    if (!group) {
      group = { vendor, minutes: 0, links: [], chartPoints: [] };
      groups.set(key, group);
    }

    const po = poForInvoice(invoice, projectId, requisitions);
    const date = invoiceChartDate(invoice);
    const invoiceLabel = (invoice.invoiceNumber || '').trim()
      ? `Invoice ${invoice.invoiceNumber.trim()}`
      : 'Invoice';

    for (const line of lines) {
      const hours = Number(line.hours);
      group.minutes += hours * 60;
      if (date) group.chartPoints.push({ minutes: hours * 60, date });
      group.links.push({
        key: `${invoice.id}:${line.id}`,
        hours,
        amount: Number(line.amount) || 0,
        purchaseRequisitionId: po.id,
        poNumber: po.poNumber,
        label: po.poNumber || invoiceLabel,
      });
    }
  }

  return Array.from(groups.values());
}
